import { CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS } from "@/lib/data/static/contactStatics";
import {
  MATTER_FILE_TAG_VALUES,
  type MatterFileTag,
} from "@/lib/utils/matterFile";
import { faker, fakerEN_US } from "@faker-js/faker";
import dayjs from "dayjs";
import {
  MATTER_LIEN_STATUS_OPTIONS,
  MATTER_TASK_STATUS_OPTIONS,
  MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS,
  MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS,
  MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
  MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS,
  MEDICAL_TREATMENT_STATUS_OPTIONS,
} from "../static/matterStatics";
import {
  MATTER_EXPENSE_CATEGORY_OPTIONS,
  MATTER_EXPENSE_PAID_BY_METHOD_OPTIONS,
  MATTER_EXPENSE_PAID_STATUS_OPTIONS,
} from "../static/matters/expenseStatics";
import { MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS } from "../static/matters/insuranceCoverageStatics";
import {
  CATEGORIES_BY_INSURANCE_TYPE,
  INSURANCE_TYPES,
  MATTER_INSURANCE_LIABILITY_OPTIONS,
} from "../static/matters/insuranceStatics";
import {
  MATTER_LIEN_CATEGORY_OPTIONS,
  MATTER_LIEN_SUBCATEGORY_OPTIONS_BY_CATEGORY,
} from "../static/matters/lienStatics";
import { MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS } from "../static/matters/settlementStatics";
import { MATTER_TASK_PRIORITY_OPTIONS } from "../static/matters/taskStatics";

const localizedFaker = fakerEN_US;

export const generateMockMedicalTreatmentPayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  return {
    matterId,
    description: localizedFaker.lorem.sentence(),
    letterOfProtection: localizedFaker.helpers.arrayElement(
      MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS,
    ),
    lien: localizedFaker.helpers.arrayElement(
      MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
    ),

    patientAccountNumber: localizedFaker.string.numeric({
      length: 11,
    }),

    planNotApplicable: false,
    billNotApplicable: false,
    recordNotApplicable: false,

    portalDetails: localizedFaker.lorem.sentence(),

    portalLink: "https://www.google.com",

    status: localizedFaker.helpers.arrayElement(
      MEDICAL_TREATMENT_STATUS_OPTIONS,
    ),

    type: localizedFaker.helpers.arrayElements(
      CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS,
      { min: 1, max: 5 },
    ),

    ...overrides,
  };
};

export const generateMockMedicalTreatmentAppointmentPayload = (
  medicalTreatmentId: string,
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const appointmentDate = dayjs(faker.date.future());

  return {
    matterId,
    medicalTreatmentId,

    billNotApplicable: false,
    recordNotApplicable: false,

    date: appointmentDate.format("YYYY-MM-DD"),

    description: localizedFaker.lorem.sentence(),

    multiday: false,
    outcome: "scheduled" as const,

    time: localizedFaker.helpers.arrayElement([
      "08:00:00",
      "09:00:00",
      "10:30:00",
      "13:00:00",
      "15:30:00",
    ]),

    type: localizedFaker.helpers.arrayElements(
      CONTACT_ORGANIZATION_MEDICAL_PROVIDER_SPECIALIZATIONS,
      { min: 1, max: 5 },
    ),

    ...overrides,
  };
};

export const generateMockMedicalTreatmentRecordPayload = (
  matterId: string,
  medicalTreatmentId: string,
  overrides: Record<string, unknown> = {},
) => {
  const status = localizedFaker.helpers.arrayElement(
    MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS,
  );

  // Base payload
  const base = {
    matterId,
    medicalTreatmentId,
    name: localizedFaker.lorem.words(3),
    status: status,
  };

  // Build status-specific fields
  const requestDate = dayjs(faker.date.past()).format("YYYY-MM-DD");

  let statusFields: Record<string, unknown> = {};

  switch (status) {
    case "Requested": {
      statusFields = {
        requestDate: requestDate,
      };
      break;
    }

    case "Received": {
      statusFields = {
        requestDate: requestDate,
        receivedDate: dayjs(requestDate)
          .add(localizedFaker.number.int({ min: 0, max: 30 }), "day")
          .format("YYYY-MM-DD"),
      };
      break;
    }

    case "Rejected": {
      statusFields = {
        requestDate: requestDate,
        rejectedDate: dayjs(requestDate)
          .add(localizedFaker.number.int({ min: 0, max: 30 }), "day")
          .format("YYYY-MM-DD"),
        rejectedReason: localizedFaker.lorem.sentence(),
      };
      break;
    }
  }

  return {
    ...base,
    ...statusFields,
    ...overrides,
  };
};

export const generateMockMedicalTreatmentBillPayload = (
  matterId: string,
  medicalTreatmentId: string,
  overrides: Record<string, unknown> = {},
) => {
  const status = localizedFaker.helpers.arrayElement(
    MEDICAL_TREATMENT_RECORD_SELECTABLE_STATUS_OPTIONS,
  );

  const originalBill = localizedFaker.number.int({ min: 50, max: 25000 });
  const goalAmount = localizedFaker.number.int({
    min: Math.max(50, Math.floor(originalBill * 0.5)),
    max: Math.max(50, Math.floor(originalBill * 1.5)),
  });

  const negotiatedAmount = localizedFaker.number.int({
    min: 0,
    max: goalAmount,
  });
  const adjustedBill = localizedFaker.number.int({
    min: 0,
    max: negotiatedAmount || goalAmount,
  });

  const base = {
    matterId,
    medicalTreatmentId,

    adjustedBill: adjustedBill,
    description: localizedFaker.lorem.sentence(),
    fileNumber: localizedFaker.number.int({
      min: 1,
      max: 9999,
    }),
    goalAmount: goalAmount,
    letterOfProtection: localizedFaker.helpers.arrayElement(
      MEDICAL_TREATMENT_LETTER_OF_PROTECTION_STATUS_OPTIONS,
    ),
    lien: localizedFaker.helpers.arrayElement(
      MEDICAL_TREATMENT_LIEN_STATUS_OPTIONS,
    ),
    negotiatedAmount: negotiatedAmount,
    originalBill: originalBill,
    recordLabel: localizedFaker.lorem.words(3),
    recordStatus: status,
    reductionFeeAgreement: localizedFaker.datatype.boolean(),
  };

  const requestDate = dayjs(faker.date.past()).format("YYYY-MM-DD");

  let statusFields: Record<string, unknown> = {};

  switch (status) {
    case "Requested": {
      statusFields = {
        recordRequestDate: requestDate,
      };
      break;
    }

    case "Received": {
      statusFields = {
        recordRequestDate: requestDate,
        recordReceivedDate: dayjs(requestDate)
          .add(localizedFaker.number.int({ min: 0, max: 30 }), "day")
          .format("YYYY-MM-DD"),
      };
      break;
    }

    case "Rejected": {
      statusFields = {
        recordRequestDate: requestDate,
        recordRejectedDate: dayjs(requestDate)
          .add(localizedFaker.number.int({ min: 0, max: 30 }), "day")
          .format("YYYY-MM-DD"),
        recordRejectedReason: localizedFaker.lorem.sentence(),
      };
      break;
    }
  }

  return {
    ...base,
    ...statusFields,
    ...overrides,
  };
};

export const generateMockMatterLienPayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const category = localizedFaker.helpers.arrayElement(
    MATTER_LIEN_CATEGORY_OPTIONS,
  );

  const subCategory = localizedFaker.helpers.arrayElement(
    MATTER_LIEN_SUBCATEGORY_OPTIONS_BY_CATEGORY[category],
  );

  const status = localizedFaker.helpers.arrayElement(
    MATTER_LIEN_STATUS_OPTIONS,
  );

  // Amounts with reasonable relationships
  const originalAmount = localizedFaker.number.int({ min: 100, max: 10000 });
  const goalAmount = localizedFaker.number.int({
    min: Math.max(0, Math.floor(originalAmount * 0.4)),
    max: Math.max(0, Math.floor(originalAmount * 1.2)),
  });
  const negotiatedAmount = localizedFaker.number.int({
    min: 0,
    max: goalAmount,
  });

  // Paid amount depends on status
  const paidAmount =
    status === "Paid"
      ? negotiatedAmount
      : status === "Partially Paid"
        ? localizedFaker.number.int({
            min: 1,
            max: Math.max(1, negotiatedAmount),
          })
        : 0;

  return {
    matterId,
    category,
    subCategory,

    label: localizedFaker.lorem.words(3),
    description: localizedFaker.lorem.sentence(),

    originalAmount,
    goalAmount,
    negotiatedAmount,
    paidAmount,

    reductionFeeAgreement: localizedFaker.datatype.boolean(),
    status,

    ...overrides,
  };
};

export const generateMockMatterInsurancePayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const insuranceType = localizedFaker.helpers.arrayElement(INSURANCE_TYPES);
  const coverageEnabled = localizedFaker.helpers.arrayElement([
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    false,
    false,
  ]);
  const isHealthInsurance = insuranceType === "Health";
  const isAutoInsurance = insuranceType === "Auto";
  const isFirstParty = localizedFaker.datatype.boolean();

  const categoryOptions = CATEGORIES_BY_INSURANCE_TYPE[insuranceType];
  const category =
    coverageEnabled && categoryOptions.length > 0
      ? localizedFaker.helpers.arrayElement(categoryOptions)
      : "";

  const policyNumber =
    coverageEnabled && !isHealthInsurance
      ? localizedFaker.finance.accountNumber()
      : "";
  const claimNumber =
    coverageEnabled && !isHealthInsurance
      ? localizedFaker.string.alphanumeric(10).toUpperCase()
      : "";
  const coverageStatus =
    coverageEnabled && !isHealthInsurance
      ? localizedFaker.helpers.arrayElement(
          MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS,
        )
      : undefined;
  const liability =
    coverageEnabled && !isHealthInsurance
      ? localizedFaker.helpers.arrayElement(MATTER_INSURANCE_LIABILITY_OPTIONS)
      : "";
  const liabilityPercentage =
    liability === "Accepted"
      ? localizedFaker.number.int({ min: 1, max: 100 })
      : null;

  const medPayEnabled =
    coverageEnabled && isAutoInsurance && isFirstParty && !isHealthInsurance
      ? localizedFaker.datatype.boolean()
      : false;
  const umEnabled =
    coverageEnabled && isAutoInsurance && isFirstParty && !isHealthInsurance
      ? localizedFaker.datatype.boolean()
      : false;
  const uimEnabled =
    coverageEnabled && isAutoInsurance && isFirstParty && !isHealthInsurance
      ? localizedFaker.datatype.boolean()
      : false;

  const medPayLimit = medPayEnabled
    ? localizedFaker.number.int({ min: 500, max: 25000 })
    : null;

  const umPerPerson = umEnabled
    ? localizedFaker.number.int({ min: 10000, max: 250000 })
    : null;
  const umPerIncident =
    umEnabled && umPerPerson !== null ? umPerPerson * 2 : null;

  const uimPerPerson = uimEnabled
    ? localizedFaker.number.int({ min: 10000, max: 250000 })
    : null;
  const uimPerIncident =
    uimEnabled && uimPerPerson !== null ? uimPerPerson * 2 : null;

  const policyLimitConfirmation =
    coverageEnabled && !isHealthInsurance && !isFirstParty
      ? localizedFaker.datatype.boolean()
      : false;
  const policyLimitPresets = [
    { person: 50000, incident: 100000 },
    { person: 100000, incident: 200000 },
    { person: null, incident: null },
  ];
  const selectedPolicyLimit =
    coverageEnabled && !isHealthInsurance && !isFirstParty
      ? localizedFaker.helpers.arrayElement(policyLimitPresets)
      : { person: null, incident: null };
  const policyLimitPerPerson =
    selectedPolicyLimit.person === null &&
    coverageEnabled &&
    !isHealthInsurance &&
    !isFirstParty
      ? localizedFaker.number.int({ min: 25000, max: 250000 })
      : selectedPolicyLimit.person;
  const policyLimitPerIncident =
    selectedPolicyLimit.person === null && policyLimitPerPerson !== null
      ? policyLimitPerPerson * 2
      : selectedPolicyLimit.incident;

  const subrogation = coverageEnabled && localizedFaker.datatype.boolean();
  const subrogationFileNumber = subrogation
    ? localizedFaker.string.alphanumeric(10).toUpperCase()
    : "";

  return {
    matterId,
    type: insuranceType,
    coverageEnabled: coverageEnabled,
    matterInsuranceCategory: category,

    policyNumber: policyNumber,
    claimNumber: claimNumber,
    coverage: coverageStatus,
    liability: liability,
    liabilityPercentage: liabilityPercentage,

    groupNumber:
      coverageEnabled && isHealthInsurance
        ? localizedFaker.string.numeric({ length: 8 })
        : "",
    memberNumber:
      coverageEnabled && isHealthInsurance
        ? localizedFaker.string.numeric({ length: 10 })
        : "",

    medPay: medPayEnabled,
    medPayLimit: medPayLimit,
    umExists: umEnabled,
    umPerPerson: umPerPerson,
    umPerIncident: umPerIncident,
    uimExists: uimEnabled,
    uimPerPerson: uimPerPerson,
    uimPerIncident: uimPerIncident,

    policyLimitConfirmation: policyLimitConfirmation,
    policyLimitPerPerson: policyLimitPerPerson,
    policyLimitPerIncident: policyLimitPerIncident,

    subrogation: subrogation,
    subrogationFileNumber: subrogationFileNumber,

    ...overrides,
  };
};

export const generateMockMatterVehiclePayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const lossOfUse = localizedFaker.datatype.boolean();

  const repairEstimate = localizedFaker.number.int({ min: 100, max: 25000 });
  const totalLoss = localizedFaker.number.int({
    min: repairEstimate,
    max: repairEstimate * 2,
  });

  return {
    matterId,
    matterInsuranceId: "",
    matterVehicleYear: localizedFaker.helpers.arrayElement(
      Array.from(
        { length: 30 },
        (_value, index) => new Date().getFullYear() - index,
      ),
    ),
    make: localizedFaker.vehicle.manufacturer(),
    odel: localizedFaker.vehicle.model(),
    color: localizedFaker.vehicle.color(),
    matterVehicleVin: localizedFaker.vehicle.vin(),
    licensePlateNumber: localizedFaker.string.alphanumeric(7).toUpperCase(),
    registrationState: localizedFaker.location.state({
      abbreviated: true,
    }),
    damageSeverity: localizedFaker.helpers.arrayElement(
      MATTER_VEHICLE_DAMAGE_SEVERITY_OPTIONS,
    ),
    mileage: localizedFaker.number.int({ min: 1000, max: 200000 }),
    repairEstimate: repairEstimate,
    totalLoss: totalLoss,
    description: localizedFaker.lorem.sentence(),
    deductiblePaidAmount: localizedFaker.number.int({
      min: 0,
      max: 5000,
    }),
    deductibleReimbursed: localizedFaker.datatype.boolean(),
    lossOfUse: lossOfUse,
    lossOfUseAmount: lossOfUse
      ? localizedFaker.number.int({ min: 100, max: 5000 })
      : null,
    lossOfUseDetails: lossOfUse ? localizedFaker.lorem.sentence() : "",
    lossOfUseReimbursed: lossOfUse ? localizedFaker.datatype.boolean() : false,
    ...overrides,
  };
};

export const generateMockMatterPropertyDamagePayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const originalAmount = localizedFaker.number.int({ min: 100, max: 50000 });
  const paymentStatus = localizedFaker.helpers.arrayElement(
    MATTER_EXPENSE_PAID_STATUS_OPTIONS,
  );
  const paymentMethod =
    paymentStatus === "Unpaid"
      ? undefined
      : localizedFaker.helpers.arrayElement(
          MATTER_EXPENSE_PAID_BY_METHOD_OPTIONS,
        );

  const paidAmount =
    paymentStatus === "Paid"
      ? originalAmount
      : paymentStatus === "Unpaid"
        ? 0
        : localizedFaker.number.int({ min: 1, max: originalAmount });

  return {
    matterId,
    label: localizedFaker.lorem.words(3),
    originalAmount,
    paymentStatus,
    paidAmount,
    paymentDate:
      paymentStatus === "Unpaid"
        ? undefined
        : dayjs(faker.date.past()).format("YYYY-MM-DD"),
    paymentMethod,
    paymentMethodDetails:
      paymentMethod === "Check"
        ? `Check #${localizedFaker.string.numeric({ length: 6 })}`
        : paymentMethod === "Credit Card"
          ? `Card ending ${localizedFaker.string.numeric({ length: 4 })}`
          : undefined,
    description: localizedFaker.lorem.sentence(),
    ...overrides,
  };
};

export const generateMockMatterExpensePayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const categoryChoice = localizedFaker.helpers.arrayElement(
    MATTER_EXPENSE_CATEGORY_OPTIONS,
  );

  const otherCategory =
    categoryChoice === "Other" ? localizedFaker.lorem.words(3) : undefined;

  const cost = localizedFaker.number.int({ min: 50, max: 20000 });
  const shouldReduce = localizedFaker.datatype.boolean();
  const reducedAmount = shouldReduce
    ? localizedFaker.number.int({ min: 0, max: cost })
    : null;
  const totalCost = reducedAmount ?? cost;

  const paidStatus = localizedFaker.helpers.arrayElement(
    MATTER_EXPENSE_PAID_STATUS_OPTIONS,
  );
  const paidByMethod =
    paidStatus === "Unpaid"
      ? ""
      : localizedFaker.helpers.arrayElement(
          MATTER_EXPENSE_PAID_BY_METHOD_OPTIONS,
        );

  const paidAmount =
    paidStatus === "Paid"
      ? totalCost
      : paidStatus === "Unpaid"
        ? 0
        : localizedFaker.number.int({ min: 1, max: totalCost || 1 });

  const refunded = localizedFaker.datatype.boolean();
  const refundedAmount =
    refunded && paidAmount
      ? localizedFaker.number.int({ min: 1, max: paidAmount })
      : null;

  return {
    matterId,
    matterExpenseLabel: localizedFaker.lorem.words(3),
    matterExpenseCategory: categoryChoice,
    matterExpenseOtherCategory: otherCategory,
    matterExpenseCost: cost,
    matterExpenseDescription: localizedFaker.lorem.sentence(),
    matterExpenseDateIncurred: dayjs(faker.date.past()).format("YYYY-MM-DD"),
    matterExpensePaidStatus: paidStatus,
    matterExpensePaidByMethod: paidByMethod,
    matterExpensePaidbyMethodDetails:
      paidByMethod === "Check"
        ? `Check #${localizedFaker.string.numeric({ length: 6 })}`
        : paidByMethod === "Credit Card"
          ? `Card ending ${localizedFaker.string.numeric({ length: 4 })}`
          : "",
    matterExpensePaidAmount: paidAmount,
    matterExpenseReducedAmount: reducedAmount,
    matterExpenseRefunded: refunded,
    matterExpenseRefundedAmount: refundedAmount,
    ...overrides,
  };
};

export const generateMockMatterSettlementDemandPayload = (
  matterId: string,
  matterInsuranceId: string,
  overrides: Record<string, unknown> = {},
) => {
  const amount = localizedFaker.number.int({ min: 5000, max: 250000 });
  const attorneyFeePercentage = localizedFaker.number.int({ min: 25, max: 45 });
  const coverageTypes = ["MedPay", "UM", "UIM"] as const;
  const coverageType = localizedFaker.datatype.boolean()
    ? localizedFaker.helpers.arrayElement(coverageTypes)
    : undefined;
  const medPayRefund =
    coverageType === "MedPay" ? localizedFaker.datatype.boolean() : false;
  const dateSent = dayjs(faker.date.past()).format("YYYY-MM-DD");
  const dateExpires = dayjs(dateSent)
    .add(localizedFaker.number.int({ min: 7, max: 45 }), "day")
    .format("YYYY-MM-DD");

  return {
    matterId,
    matterInsuranceId,
    matterSettlementDemandPolicyLimit: localizedFaker.datatype.boolean(),
    matterSettlementDemandAmount: amount,
    matterSettlementDemandDateExpires: dateExpires,
    matterSettlementDemandDetails: localizedFaker.lorem.sentence(),
    matterSettlementDemandDateSent: dateSent,
    matterSettlementDemandAttorneyFeePercentage: attorneyFeePercentage,
    matterSettlementDemandAttorneyFeeAmount: Math.round(
      amount * (attorneyFeePercentage / 100),
    ),
    matterSettlementDemandCoverageType: coverageType,
    matterSettlementDemandMedPayRefund: medPayRefund,
    matterSettlementDemandMedPayRefundAmount: medPayRefund
      ? localizedFaker.number.int({ min: 100, max: amount })
      : null,
    ...overrides,
  };
};

export const generateMockMatterSettlementOfferPayload = (
  matterId: string,
  matterInsuranceId: string,
  overrides: Record<string, unknown> = {},
) => {
  const amount = localizedFaker.number.int({ min: 2500, max: 150000 });
  const attorneyFeePercentage = localizedFaker.number.int({ min: 25, max: 45 });
  const dateSent = dayjs(faker.date.past()).format("YYYY-MM-DD");
  const accepted = localizedFaker.datatype.boolean();
  const acceptedStatus = accepted
    ? localizedFaker.helpers.arrayElement(
        MATTER_SETTLEMENT_MONEY_STATUS_OPTIONS,
      )
    : undefined;
  const coverageTypes = ["MedPay", "UM", "UIM"] as const;
  const coverageType = localizedFaker.datatype.boolean()
    ? localizedFaker.helpers.arrayElement(coverageTypes)
    : undefined;
  const medPay = coverageType === "MedPay";
  const medPayRefund = medPay ? localizedFaker.datatype.boolean() : false;

  return {
    matterId,
    matterInsuranceId,
    matterSettlementOfferPolicyLimit: localizedFaker.datatype.boolean(),
    matterSettlementOfferAmount: amount,
    matterSettlementOfferDetails: localizedFaker.lorem.sentence(),
    matterSettlementOfferDateSent: dateSent,
    matterSettlementOfferDateReceived: dateSent,
    matterSettlementOfferAccepted: accepted,
    matterSettlementOfferAcceptedDate: accepted
      ? dayjs(dateSent)
          .add(localizedFaker.number.int({ min: 0, max: 30 }), "day")
          .format("YYYY-MM-DD")
      : "",
    matterSettlementOfferStatus: acceptedStatus,
    matterSettlementOfferAttorneyFeePercentage: attorneyFeePercentage,
    matterSettlementOfferAttorneyFeeAmount: Math.round(
      amount * (attorneyFeePercentage / 100),
    ),
    matterSettlementOfferCoverageType: coverageType,
    matterSettlementOfferMedPay: medPay,
    matterSettlementOfferMedPayRefund: medPayRefund,
    matterSettlementOfferMedPayRefundAmount: medPayRefund
      ? localizedFaker.number.int({ min: 100, max: amount })
      : null,
    ...overrides,
  };
};

export const generateMockMatterTaskPayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const status = localizedFaker.helpers.arrayElement(
    MATTER_TASK_STATUS_OPTIONS,
  );
  const isCompleted = status === "Completed";
  const startDate = dayjs(faker.date.recent({ days: 30 }));
  const dueDate = startDate.add(
    localizedFaker.number.int({ min: 1, max: 30 }),
    "day",
  );

  return {
    matterId,
    title: localizedFaker.lorem.words(4),
    description: localizedFaker.lorem.sentence(),
    status: status,
    priority: localizedFaker.helpers.arrayElement(MATTER_TASK_PRIORITY_OPTIONS),
    startDate: startDate.format("YYYY-MM-DD"),
    dueDate: dueDate.format("YYYY-MM-DD"),
    completedDate: isCompleted ? dayjs().format("YYYY-MM-DD") : "",
    ...overrides,
  };
};

export const generateMockMatterNotePayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  return {
    matterId,
    message: localizedFaker.lorem.paragraphs({ min: 1, max: 2 }),
    draft: localizedFaker.datatype.boolean(),
    pinned: localizedFaker.datatype.boolean(),
    ...overrides,
  };
};

const MOCK_MATTER_FILE_PRESIGNED_URL_KEY =
  "dce8a6eb6f89428897a01263c9d876d4.png";

const buildMockMatterFileName = () => {
  const extension = localizedFaker.helpers.arrayElement([
    "pdf",
    "jpg",
    "png",
    "docx",
  ]);
  const name = localizedFaker.lorem.words(3).replace(/\s+/g, "-");
  return `${name}.${extension}`;
};

export const generateMockMatterFilePayload = (
  matterId: string,
  overrides: Record<string, unknown> = {},
) => {
  const hasCriticalTag = localizedFaker.datatype.boolean();
  const criticalTags: MatterFileTag[] = [];

  if (hasCriticalTag) {
    criticalTags.push(
      localizedFaker.helpers.arrayElement([...MATTER_FILE_TAG_VALUES]),
    );
  }

  return {
    matterId,
    isFolder: false,
    idLocation: "",
    name: buildMockMatterFileName(),
    description: localizedFaker.lorem.sentence(),
    presignedUrlKey: MOCK_MATTER_FILE_PRESIGNED_URL_KEY,
    tags: criticalTags,
    ...overrides,
  };
};

export const generateMockMatterLitigationDatePayload = (
  matterId: string,
  typeId: string,
  overrides: Record<string, unknown> = {},
) => {
  const deadlineDate = dayjs(faker.date.future());
  const completed = localizedFaker.datatype.boolean();

  return {
    matterId,
    typeId,
    deadlineDate: deadlineDate.format("YYYY-MM-DD"),
    deadlineTime: localizedFaker.helpers.arrayElement([
      "08:00",
      "10:30",
      "13:00",
      "15:30",
    ]),
    completedDate: completed
      ? deadlineDate
          .add(localizedFaker.number.int({ min: -7, max: 2 }), "day")
          .format("YYYY-MM-DD")
      : "",
    confirmation: localizedFaker.datatype.boolean(),
    description: localizedFaker.lorem.sentence(),
    ...overrides,
  };
};
